import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import { handshake, postToDriver } from '../helpers/driverBridge';

const MAX_CAPTURE_DIM = 1600;

// Bedrock rejects images > 5 MiB at message-validation time and the run
// dies before the model sees anything. Cap a safe margin under that with
// a fallback ladder: requested format → JPEG q0.85 → progressively lower
// JPEG quality → downscale 30 % + JPEG q0.5. The base64 expansion factor
// is 4/3, so we cap on dataUrl.length directly to avoid round-tripping
// through atob() to count bytes.
const MAX_IMAGE_BYTES = 4 * 1024 * 1024;

function encodeWithMaxBytes(canvas, preferredFormat, preferredQuality) {

    const maxLen = Math.floor(MAX_IMAGE_BYTES * 4 / 3) + 100;
    const tryEncode = (c, fmt, q) => {

        const url = fmt === 'jpeg'
            ? c.toDataURL('image/jpeg', q)
            : c.toDataURL('image/png');
        return url.length <= maxLen ? { dataUrl: url, format: fmt } : null;

    };

    let result = tryEncode(canvas, preferredFormat, preferredQuality);
    if (result) {

        console.info(`[ft-hd] encoded ${preferredFormat} at q=${preferredQuality} — ${(result.dataUrl.length / 1024 / 1024 * 0.75).toFixed(2)} MiB`);
        return result;

    }

    for (const q of [preferredQuality, 0.7, 0.55, 0.4]) {

        result = tryEncode(canvas, 'jpeg', q);
        if (result) {

            console.info(`[ft-hd] ${preferredFormat} too large; fell back to JPEG q=${q} — ${(result.dataUrl.length / 1024 / 1024 * 0.75).toFixed(2)} MiB`);
            return result;

        }

    }

    const w = Math.max(1, Math.floor(canvas.width * 0.7));
    const h = Math.max(1, Math.floor(canvas.height * 0.7));
    const small = document.createElement('canvas');
    small.width = w;
    small.height = h;
    small.getContext('2d').drawImage(canvas, 0, 0, w, h);
    const fallbackUrl = small.toDataURL('image/jpeg', 0.5);
    console.info(`[ft-hd] all JPEG qualities too large; downscaled+JPEG q=0.5 — ${(fallbackUrl.length / 1024 / 1024 * 0.75).toFixed(2)} MiB`);
    return {
        canvas: small,
        dataUrl: fallbackUrl,
        format: 'jpeg'
    };

}

/**
 * Owns the iframe for the target site and exposes an imperative
 * executeTool(toolName, toolInput, credentials) method to the parent run loop.
 *
 * Internally tracks driver readiness — every iframe load (initial or
 * navigation) re-runs the handshake, and pending tool calls await it before
 * dispatching.
 *
 * Optionally hosts a high-fidelity capture stream (getDisplayMedia + Region
 * Capture) so screenshot / capture_evidence return real browser pixels
 * instead of html2canvas's DOM reconstruction. Driver-side html2canvas is
 * the fallback for selector-targeted screenshots and when HD is off.
 */
export const Viewport = forwardRef(({ initialUrl, onError, onHDChange, onIframeLoad, onReady, sessionKey }, ref) => {

    const iframeRef = useRef(null);
    const driverReadyRef = useRef(null);

    // Hold callbacks in a ref so they don't go in the effect deps. Including
    // them would re-run the effect on every parent render and re-assigning
    // iframe.src forces a reload in some browsers, tearing down the driver
    // between tool calls.
    const callbacksRef = useRef({ onError, onHDChange, onIframeLoad, onReady });
    useEffect(() => {

        callbacksRef.current = { onError, onHDChange, onIframeLoad, onReady };

    }, [onReady, onError, onHDChange, onIframeLoad]);

    // HD capture state — getDisplayMedia stream, the video sink we draw
    // frames from, and which crop strategy is in play. Stored in a ref so
    // re-renders never tear down the active stream.
    // mode is 'region' (CropTarget+cropTo, Chromium) or 'jscrop' (full tab + manual crop, fallback).
    const hdRef = useRef({
        enabled: false,
        mode: null,
        stream: null,
        track: null,
        video: null
    });

    const teardownHD = useCallback(() => {

        const hd = hdRef.current;
        try {

            hd.stream && hd.stream.getTracks().forEach(t => t.stop());

        } catch (_) { /* */ }
        if (hd.video) {

            try {

                hd.video.srcObject = null;

            } catch (_) { /* */ }
            if (hd.video.parentNode) hd.video.parentNode.removeChild(hd.video);

        }
        hdRef.current = { enabled: false, mode: null, stream: null, track: null, video: null };
        callbacksRef.current.onHDChange?.({ enabled: false });

    }, []);

    useEffect(() => () => teardownHD(), [teardownHD]);

    useEffect(() => {

        if (!initialUrl) return;

        const iframe = iframeRef.current;
        if (!iframe) return;

        // Force the credentialless attribute imperatively before iframe.src
        // triggers any load. React passes through the JSX-level
        // `credentialless=""` attribute in most setups, but its serialization
        // of unrecognized boolean attributes has historically been flaky;
        // setting it via ref guarantees the iframe enters credentialless
        // mode before the browser starts the load.
        iframe.setAttribute('credentialless', '');

        const onLoad = () => {

            // Fire onIframeLoad BEFORE handshake completes — handshake polls
            // for the driver script to ack and can take 15s if the script
            // failed to load. Anything time-sensitive that just needs the
            // iframe to have rendered content (HD capture's cropTo, e.g.)
            // can use this earlier signal.
            callbacksRef.current.onIframeLoad?.();

            driverReadyRef.current = handshake(iframe)
                .then(() => {

                    callbacksRef.current.onReady?.();

                })
                .catch(e => {

                    callbacksRef.current.onError?.(e);

                });

        };

        iframe.addEventListener('load', onLoad);
        iframe.src = initialUrl;

        return () => iframe.removeEventListener('load', onLoad);

    }, [initialUrl]);

    /**
     * Capture a frame from the active HD stream into a PNG, mirroring the
     * shape the driver's html2canvas path returns so the orchestrator
     * doesn't care which path produced the image.
     *
     * In 'region' mode the stream is already cropped to the iframe by the
     * browser compositor — we just paint the video frame as-is. In
     * 'jscrop' mode we receive the entire tab and pull the iframe rect
     * out by mapping CSS coordinates to bitmap coordinates; the video
     * dimensions tell us the scale (effectively devicePixelRatio).
     */
    const captureFromStream = useCallback(async (toolName, toolInput) => {

        const hd = hdRef.current;
        const iframe = iframeRef.current;
        if (!hd.enabled || !hd.video || !iframe) throw new Error('hd capture unavailable');
        if (!hd.video.videoWidth || !hd.video.videoHeight) throw new Error('hd stream not ready');

        let canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (hd.mode === 'region') {

            canvas.width = hd.video.videoWidth;
            canvas.height = hd.video.videoHeight;
            ctx.drawImage(hd.video, 0, 0);

        } else {

            const rect = iframe.getBoundingClientRect();
            if (rect.width <= 0 || rect.height <= 0) throw new Error('iframe has no rendered area');

            const scaleX = hd.video.videoWidth / window.innerWidth;
            const scaleY = hd.video.videoHeight / window.innerHeight;
            const sx = Math.max(0, rect.left * scaleX);
            const sy = Math.max(0, rect.top * scaleY);
            const sw = Math.max(1, Math.min(hd.video.videoWidth - sx, rect.width * scaleX));
            const sh = Math.max(1, Math.min(hd.video.videoHeight - sy, rect.height * scaleY));
            canvas.width = Math.round(sw);
            canvas.height = Math.round(sh);
            ctx.drawImage(hd.video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

        }

        if (canvas.width > MAX_CAPTURE_DIM || canvas.height > MAX_CAPTURE_DIM) {

            const ratio = Math.min(MAX_CAPTURE_DIM / canvas.width, MAX_CAPTURE_DIM / canvas.height);
            const out = document.createElement('canvas');
            out.width = Math.max(1, Math.round(canvas.width * ratio));
            out.height = Math.max(1, Math.round(canvas.height * ratio));
            out.getContext('2d').drawImage(canvas, 0, 0, out.width, out.height);
            canvas = out;

        }

        // Ask the driver for the current iframe URL so capture_evidence
        // records the right artifact metadata. Best-effort — if the driver
        // hasn't handshaken yet we just omit it.
        let pageUrl = null;
        if (toolName === 'capture_evidence') {

            try {

                const state = await postToDriver(iframe, '_page_state', {});
                pageUrl = (state && state.url) || null;

            } catch (_) { /* */ }

        }

        // JPEG for the auto-screenshot (the model's vision input), PNG for
        // capture_evidence so the QA-reviewer artifact stays sharp. Both
        // go through encodeWithMaxBytes — Bedrock rejects images > 5 MiB
        // and HD-captured PNGs of dense pages can blow past that.
        const preferred = toolName === 'capture_evidence' ? 'png' : 'jpeg';
        const encoded = encodeWithMaxBytes(canvas, preferred, 0.85);
        const finalCanvas = encoded.canvas || canvas;
        const result = {
            format: encoded.format,
            height: finalCanvas.height,
            image_base64: encoded.dataUrl.slice(encoded.dataUrl.indexOf(',') + 1),
            width: finalCanvas.width
        };
        if (toolName === 'screenshot') result.target = (toolInput && toolInput.selector) || 'page';
        if (toolName === 'capture_evidence') {

            result.label = (toolInput && toolInput.label) || 'unlabeled';
            result.url = pageUrl;

        }
        return result;

    }, []);

    useImperativeHandle(ref, () => ({

        disableHighFidelityCapture() {

            teardownHD();

        },

        async enableHighFidelityCapture(opts = {}) {

            const iframe = iframeRef.current;
            if (!iframe) return { ok: false, reason: 'no-iframe' };

            if (hdRef.current.enabled) return { mode: hdRef.current.mode, ok: true };

            // Two entry points: (1) opts.stream — Runner pre-acquired the
            // stream during the Start-button click handler so the user
            // activation was still fresh; this method just adopts it. (2) no
            // opts.stream — manual button click, we request the stream here.
            let stream = opts.stream || null;
            if (!stream) {

                if (!navigator.mediaDevices || typeof navigator.mediaDevices.getDisplayMedia !== 'function')
                    return { ok: false, reason: 'unsupported' };

                try {

                    // preferCurrentTab + selfBrowserSurface are Chromium-only;
                    // non-Chromium browsers ignore them and show the full picker.
                    stream = await navigator.mediaDevices.getDisplayMedia({
                        audio: false,
                        preferCurrentTab: true,
                        selfBrowserSurface: 'include',
                        video: { displaySurface: 'browser' }
                    });

                } catch (e) {

                    return { error: e.message, ok: false, reason: e.name === 'NotAllowedError' ? 'denied' : 'error' };

                }

            }

            const track = stream.getVideoTracks()[0];
            if (!track) {

                stream.getTracks().forEach(t => t.stop());
                return { ok: false, reason: 'no-video-track' };

            }

            // Hard timeout for the post-getDisplayMedia adoption phase.
            // CropTarget.cropTo and video.play() have both been seen to hang
            // in some tab/window scenarios — without a ceiling, the sidebar
            // sits at "Requesting share…" forever and the run never picks
            // up HD capture (or fails to fall back to html2canvas).
            const HD_ADOPTION_TIMEOUT_MS = 8000;

            const adopt = async () => {

                let mode = 'jscrop';
                const cropTargetSupported
                    = typeof window.CropTarget !== 'undefined'
                    && typeof window.CropTarget.fromElement === 'function'
                    && typeof track.cropTo === 'function';
                if (cropTargetSupported)
                    try {

                        const target = await window.CropTarget.fromElement(iframe);
                        await track.cropTo(target);
                        mode = 'region';

                    } catch (_) { /* fall back to jscrop */ }

                const video = document.createElement('video');
                video.muted = true;
                video.playsInline = true;
                video.srcObject = stream;
                video.style.cssText = 'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;pointer-events:none';
                document.body.appendChild(video);

                await video.play();

                return { mode, video };

            };

            let adopted;
            try {

                adopted = await Promise.race([
                    adopt(),
                    new Promise((_, rej) => setTimeout(() => rej(new Error(`HD adoption timed out after ${HD_ADOPTION_TIMEOUT_MS}ms`)), HD_ADOPTION_TIMEOUT_MS))
                ]);

            } catch (e) {

                // Don't stop the tracks — the user already granted share
                // and the stream is still live. Return it so the caller can
                // retry adoption (e.g. via the manual Enable HD button)
                // without re-prompting for permissions.
                return { error: e.message, ok: false, reason: 'adoption-failed', stream };

            }

            track.addEventListener('ended', teardownHD);

            hdRef.current = { enabled: true, mode: adopted.mode, stream, track, video: adopted.video };
            callbacksRef.current.onHDChange?.({ enabled: true, mode: adopted.mode });
            return { mode: adopted.mode, ok: true };

        },

        async executeTool(toolName, toolInput, credentials) {

            const iframe = iframeRef.current;
            if (!iframe) throw new Error('iframe not mounted');

            if (driverReadyRef.current) await driverReadyRef.current;

            // Bridge owns navigation — the driver context dies before its
            // result postMessage can land. Trigger the load and re-handshake.
            //
            // After the new page settles we compare its url + body fingerprint
            // against the pre-nav state. SPAs that share a shell document
            // sometimes accept iframe.src changes without re-rendering the
            // route (the load event fires on the cached shell). Without this
            // check, the LLM sees `{ ok: true, url: requested }` and thinks
            // it moved when nothing actually changed — runs spiral chasing
            // ghosts. We surface a warning instead of silently succeeding.
            if (toolName === 'navigate') {

                let before = null;
                try {

                    before = await postToDriver(iframe, '_page_state', {});

                } catch (_) { /* first-load: no driver yet, that's fine */ }

                driverReadyRef.current = new Promise((resolve, reject) => {

                    const onLoad = () => {

                        iframe.removeEventListener('load', onLoad);
                        handshake(iframe).then(resolve).catch(reject);

                    };
                    iframe.addEventListener('load', onLoad);

                });

                iframe.src = toolInput.url;
                await driverReadyRef.current;

                // Small grace period for SPAs to finish their initial route render.
                await new Promise(r => setTimeout(r, 350));

                let after = null;
                try {

                    after = await postToDriver(iframe, '_page_state', {});

                } catch (_) { /* if we can't read the state, fall through with no warning */ }

                const result = { ok: true, url: (after && after.url) || toolInput.url };

                if (before && after) {

                    const sameUrl = before.url === after.url;
                    const sameContent = before.bodyTextHash === after.bodyTextHash
                        && before.bodyTextLen === after.bodyTextLen;
                    if (sameUrl && sameContent)
                        result.warning = `Navigation completed but the page URL and content are unchanged from before. The route likely did not render — try a different approach (a hard reload, clicking a link instead, or returning to the parent view).`;
                    else if (sameContent)
                        result.warning = `URL changed to ${after.url} but the page content is identical to the previous page. The new route may not have rendered.`;

                }

                return result;

            }

            // Substitute credentials before the value enters the iframe.
            if (toolName === 'type_credential') {

                const value = credentials && credentials[toolInput.which];
                if (!value)
                    throw new Error(`No ${toolInput.which} credential provided in this session`);

                return await postToDriver(iframe, 'type', {
                    selector: toolInput.selector,
                    text: value
                });

            }

            // High-fidelity capture path: when the user has granted display
            // sharing, paint the frame off the live video stream instead of
            // running html2canvas inside the iframe. Falls through to the
            // driver if HD failed for any reason, AND for selector-targeted
            // screenshots — those need an element-level capture, which the
            // tab-level stream can't provide.
            if ((toolName === 'screenshot' || toolName === 'capture_evidence') && hdRef.current.enabled) {

                const isFullPage = toolName === 'capture_evidence' || !(toolInput && toolInput.selector);
                if (isFullPage) {

                    try {

                        return await captureFromStream(toolName, toolInput);

                    } catch (e) {

                        // Stream-side failure (track ended, video not ready,
                        // iframe detached) — quietly fall through to the
                        // driver path. The model still gets an image.
                        console.warn(`HD capture failed, falling back to driver: ${e.message}`);

                    }

                }

            }

            return await postToDriver(iframe, toolName, toolInput);

        },

        isHighFidelityActive() {

            return hdRef.current.enabled;

        }

    }));

    return (
        <div className="flex-grow-1 position-relative bg-light">
            {!initialUrl && (
                <div className="position-absolute top-50 start-50 translate-middle text-muted">
                    Submit a run to load the target site here
                </div>
            )}
            {/*
                `credentialless` (Chrome/Edge 110+) gives the iframe a fresh
                ephemeral context: empty cookie jar, empty localStorage /
                sessionStorage / IndexedDB, isolated cache. Cookies set during
                the test session work as expected within the iframe lifetime;
                they're discarded when the iframe is replaced or the run ends.
                Tests start clean — the analyst's existing logins for the
                target site never leak into the run.

                Older browsers (Safari) ignore the attribute and fall back to
                normal behavior. For those, modern third-party cookie
                partitioning still gives some isolation, but it's not as
                strict.
            */}
            <iframe
                credentialless=""
                key={sessionKey || 'viewport-iframe'}
                ref={iframeRef}
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
                style={{ border: 'none', display: initialUrl ? 'block' : 'none', height: '100%', width: '100%' }}
                title="target-site"
            />
        </div>
    );

});

Viewport.displayName = 'Viewport';
